import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'motion/react'
import { useMediaQuery } from '@/components/motion'
import { cn } from '@/lib/utils'

export interface TornadoCard {
  id: string
  /** Face shown while the card turns toward the viewer. */
  front: React.ReactNode
  /** Face shown while it turns away (keeps text from reading mirrored). */
  back: React.ReactNode
}

interface CardTornadoProps {
  cards: TornadoCard[]
  className?: string
}

/* Geometry from the layout spec (docs/design/b2p-page-layouts.md §3). */
const STEP_DEG = 32 // angle between neighbouring cards
const DROP = 0.4 // each card sits this many card heights below the previous
const RADIUS_EM = 42
const PERSPECTIVE_EM = 75
const CARD_W_EM = 12
const CARD_H_EM = 16
const BASE_SPEED = 5 // deg/s of idle rotation
const MAX_DIM = 0.25 // back cards fade by up to 25%
const MAX_BLUR_EM = 0.5
const FRICTION = 1.6 // decay rate of added velocity, per second
const EDGE_FADE = 'linear-gradient(to bottom, transparent 0%, #000 30%, #000 70%, transparent 100%)'

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

/**
 * Wrap a card's helix position into [-n/2, n/2) so the column reads as
 * endless: cards leaving at the top re-enter at the bottom, far outside the
 * faded edges.
 */
function wrap(p: number, n: number) {
  return ((((p + n / 2) % n) + n) % n) - n / 2
}

function Helix({ cards, still, interactive }: { cards: TornadoCard[]; still: boolean; interactive: boolean }) {
  const stageRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])
  const faceRefs = useRef<Array<[HTMLDivElement | null, HTMLDivElement | null]>>([])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const n = cards.length
    const blurLevel = new Array<number>(n).fill(-1)
    const st = {
      rot: 0,
      vel: 0,
      last: 0,
      raf: 0,
      inView: false,
      dragging: false,
      lastX: 0,
      lastT: 0,
      dragVel: 0,
    }

    // Writes transforms straight to the DOM: no React render per frame.
    const apply = () => {
      for (let i = 0; i < n; i++) {
        const el = cardRefs.current[i]
        if (!el) continue
        const p = wrap(i - st.rot / STEP_DEG, n)
        const angle = p * STEP_DEG
        el.style.transform = `translate3d(0, ${(p * DROP * CARD_H_EM).toFixed(3)}em, 0) rotateY(${angle.toFixed(2)}deg) translateZ(${RADIUS_EM}em)`
        // 0 facing the viewer, 1 at the very back of the helix.
        const depth = (1 - Math.cos((angle * Math.PI) / 180)) / 2
        const opacity = (1 - MAX_DIM * depth).toFixed(3)
        // Blur is stepped so filters only change a few times per turn.
        const level = depth < 0.3 ? 0 : depth < 0.7 ? 1 : 2
        for (const face of faceRefs.current[i] ?? []) {
          if (!face) continue
          face.style.opacity = opacity
          if (level !== blurLevel[i]) face.style.filter = level === 0 ? 'none' : `blur(${(MAX_BLUR_EM * level) / 2}em)`
        }
        blurLevel[i] = level
      }
    }

    // Start mid-turn so the resting layout shows cards on both sides.
    st.rot = STEP_DEG * 0.5
    apply()
    if (still) return

    const frame = (t: number) => {
      const dt = st.last ? Math.min((t - st.last) / 1000, 0.05) : 0
      st.last = t
      if (!st.dragging) {
        st.rot += (BASE_SPEED + st.vel) * dt
        st.vel *= Math.exp(-dt * FRICTION)
      }
      apply()
      st.raf = requestAnimationFrame(frame)
    }
    const start = () => {
      if (st.raf || !st.inView || document.hidden) return
      st.last = 0
      st.raf = requestAnimationFrame(frame)
    }
    const stop = () => {
      cancelAnimationFrame(st.raf)
      st.raf = 0
    }

    const io = new IntersectionObserver(([entry]) => {
      st.inView = !!entry?.isIntersecting
      if (st.inView) start()
      else stop()
    })
    io.observe(stage)
    const onVisibility = () => (document.hidden ? stop() : start())
    document.addEventListener('visibilitychange', onVisibility)

    // Wheel and drag add velocity; the page still scrolls normally.
    const onWheel = (e: WheelEvent) => {
      st.vel = clamp(st.vel + e.deltaY * 0.08, -180, 180)
    }
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'touch' || e.button !== 0) return
      st.dragging = true
      st.lastX = e.clientX
      st.lastT = performance.now()
      st.dragVel = 0
      stage.setPointerCapture(e.pointerId)
      stage.style.cursor = 'grabbing'
    }
    const onMove = (e: PointerEvent) => {
      if (!st.dragging) return
      const now = performance.now()
      const dx = e.clientX - st.lastX
      const dt = Math.max((now - st.lastT) / 1000, 0.001)
      // Dragging right swings the front card right.
      st.rot -= dx * 0.2
      st.dragVel = st.dragVel * 0.6 + ((-dx * 0.2) / dt) * 0.4
      st.lastX = e.clientX
      st.lastT = now
    }
    const onUp = (e: PointerEvent) => {
      if (!st.dragging) return
      st.dragging = false
      st.vel = clamp(st.dragVel - BASE_SPEED, -240, 240)
      if (stage.hasPointerCapture(e.pointerId)) stage.releasePointerCapture(e.pointerId)
      stage.style.cursor = ''
    }
    if (interactive) {
      stage.addEventListener('wheel', onWheel, { passive: true })
      stage.addEventListener('pointerdown', onDown)
      stage.addEventListener('pointermove', onMove)
      stage.addEventListener('pointerup', onUp)
      stage.addEventListener('pointercancel', onUp)
    }

    return () => {
      stop()
      io.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      stage.removeEventListener('wheel', onWheel)
      stage.removeEventListener('pointerdown', onDown)
      stage.removeEventListener('pointermove', onMove)
      stage.removeEventListener('pointerup', onUp)
      stage.removeEventListener('pointercancel', onUp)
    }
  }, [cards, still, interactive])

  return (
    <div className="h-full w-full" style={{ maskImage: EDGE_FADE, WebkitMaskImage: EDGE_FADE }}>
      <div
        ref={stageRef}
        className={cn('relative h-full w-full overflow-hidden select-none', interactive && !still && 'cursor-grab')}
        style={{ perspective: `${PERSPECTIVE_EM}em`, touchAction: 'pan-y' }}
      >
        <div
          className="absolute left-1/2 top-1/2"
          style={{ transformStyle: 'preserve-3d', transform: `translateZ(-${RADIUS_EM}em)` }}
        >
          {cards.map((card, i) => (
            <div
              key={card.id}
              ref={(el) => {
                cardRefs.current[i] = el
              }}
              className="absolute will-change-transform"
              style={{
                width: `${CARD_W_EM}em`,
                height: `${CARD_H_EM}em`,
                marginLeft: `-${CARD_W_EM / 2}em`,
                marginTop: `-${CARD_H_EM / 2}em`,
                transformStyle: 'preserve-3d',
              }}
            >
              <div
                ref={(el) => {
                  faceRefs.current[i] = [el, faceRefs.current[i]?.[1] ?? null]
                }}
                className="absolute inset-0 [backface-visibility:hidden]"
              >
                {card.front}
              </div>
              <div
                ref={(el) => {
                  faceRefs.current[i] = [faceRefs.current[i]?.[0] ?? null, el]
                }}
                className="absolute inset-0 [backface-visibility:hidden]"
                style={{ transform: 'rotateY(180deg)' }}
              >
                {card.back}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Below md: a still, overlapping fan of the first four cards. */
function StaticFan({ cards }: { cards: TornadoCard[] }) {
  const fan = cards.slice(0, 4)
  const tilt = [-9, -3, 3, 9]
  const lift = [1.2, 0.2, 0, 0.9]
  return (
    <div className="flex justify-center py-6 text-[clamp(8px,2.6vw,11px)]">
      {fan.map((card, i) => (
        <div
          key={card.id}
          className="relative shrink-0 shadow-[0_18px_30px_-18px_rgba(10,10,10,0.45)]"
          style={{
            width: `${CARD_W_EM}em`,
            height: `${CARD_H_EM}em`,
            marginLeft: i === 0 ? 0 : `-${CARD_W_EM * 0.42}em`,
            transform: `translateY(${lift[i]}em) rotate(${tilt[i]}deg)`,
            zIndex: i === 1 || i === 2 ? 2 : 1,
          }}
        >
          {card.front}
        </div>
      ))}
    </div>
  )
}

/**
 * 3D "card tornado" from the reference's works hero: cards on a vertical
 * helix that turns slowly on its own, faded and blurred toward the back.
 * Wheel and drag add spin on desktop. Runs only while on screen; static
 * under reduced motion; a flat fan below md. Decorative.
 */
export function CardTornado({ cards, className }: CardTornadoProps) {
  const reduce = useReducedMotion() ?? false
  const wide = useMediaQuery('(min-width: 768px)')
  const desktop = useMediaQuery('(min-width: 1024px) and (hover: hover) and (pointer: fine)')

  return (
    <div aria-hidden className={cn('relative', className)}>
      {wide ? (
        <div className="h-full w-full text-[clamp(10px,1.05vw,17px)]">
          <Helix cards={cards} still={reduce} interactive={desktop && !reduce} />
        </div>
      ) : (
        <StaticFan cards={cards} />
      )}
    </div>
  )
}
